import { useEffect, useState } from "react";
import { ArrowLeft, Sliders, Disc3, Music, Clock, Check, Loader2 } from "lucide-react";
import { motion } from "framer-motion";
import PayPalCheckoutModal from "./PayPalCheckoutModal";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const CATEGORY_META = {
  mix: { label: "Mix Pro", icon: Sliders, accent: "text-amber-400", chip: "bg-amber-500/10 border-amber-500/30 text-amber-300" },
  mastering: { label: "Mastering", icon: Disc3, accent: "text-emerald-400", chip: "bg-emerald-500/10 border-emerald-500/30 text-emerald-300" },
  production: { label: "Production", icon: Music, accent: "text-rose-400", chip: "bg-rose-500/10 border-rose-500/30 text-rose-300" },
};

export default function ServicesProPage({ onBack }) {
  const [services, setServices] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selected, setSelected] = useState(null);
  const [bookingState, setBookingState] = useState({ name: "", email: "", project: "", notes: "" });
  const [paypalOpen, setPaypalOpen] = useState(false);
  const [bookingResult, setBookingResult] = useState(null);

  useEffect(() => {
    fetch(`${API}/services-pro`)
      .then((r) => r.json())
      .then((d) => { setServices(d || []); setLoading(false); })
      .catch(() => setLoading(false));
  }, []);

  const grouped = services.reduce((acc, s) => {
    (acc[s.category] = acc[s.category] || []).push(s);
    return acc;
  }, {});

  const handleSelect = (svc) => {
    setSelected(svc);
    setBookingResult(null);
    setBookingState({ name: "", email: "", project: "", notes: "" });
    setTimeout(() => {
      document.getElementById("booking-form")?.scrollIntoView({ behavior: "smooth", block: "start" });
    }, 80);
  };

  const validForm = selected && bookingState.name.trim() && /@/.test(bookingState.email) && bookingState.email.includes(".");

  const registerBooking = async (orderId) => {
    const res = await fetch(`${API}/services-pro/book`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({
        service_id: selected.id,
        customer_name: bookingState.name.trim(),
        customer_email: bookingState.email.trim(),
        project_title: bookingState.project.trim(),
        notes: bookingState.notes.trim(),
        paypal_order_id: orderId,
      }),
    });
    if (!res.ok) throw new Error(`Booking registration failed: ${res.status}`);
    return res.json();
  };

  return (
    <main className="min-h-screen bg-black text-white pt-24 pb-20 px-4 sm:px-6 lg:px-10">
      <div className="max-w-6xl mx-auto">
        <button data-testid="services-back-btn" onClick={onBack}
          className="inline-flex items-center gap-2 text-zinc-400 hover:text-yellow-400 text-sm mb-6 transition-colors">
          <ArrowLeft size={16} /> Retour
        </button>

        <header className="mb-12">
          <p className="text-amber-400 text-xs tracking-[0.3em] uppercase mb-3">Services KAZAJAH STUDIO</p>
          <h1 className="text-4xl sm:text-5xl lg:text-6xl font-black leading-[0.95] tracking-tight">
            Mix, Mastering<br />
            <span className="text-amber-400">& Production Pro</span>
          </h1>
          <p className="text-zinc-400 mt-6 max-w-2xl text-base sm:text-lg leading-relaxed">
            Studio professionnel signé DJHALLMANN · La Cattedrale (Milan) · Guadeloupe.
            Choisis ton service, paie en sécurité avec PayPal — on te recontacte sous 24h.
          </p>
        </header>

        {loading ? (
          <div className="flex items-center justify-center py-20"><Loader2 size={28} className="animate-spin text-amber-400" /></div>
        ) : (
          <div className="space-y-14">
            {Object.entries(grouped).map(([cat, items]) => {
              const meta = CATEGORY_META[cat] || { label: cat, icon: Music, accent: "text-white", chip: "bg-zinc-800 text-zinc-300" };
              const Icon = meta.icon;
              return (
                <section key={cat} data-testid={`services-section-${cat}`}>
                  <div className="flex items-center gap-3 mb-6">
                    <Icon size={20} className={meta.accent} />
                    <h2 className={`text-2xl font-bold ${meta.accent}`}>{meta.label}</h2>
                  </div>
                  <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-5">
                    {items.map((svc) => {
                      const isSelected = selected?.id === svc.id;
                      return (
                        <motion.div key={svc.id}
                          initial={{ opacity: 0, y: 16 }} whileInView={{ opacity: 1, y: 0 }}
                          viewport={{ once: true }} transition={{ duration: 0.4 }}
                          data-testid={`service-card-${svc.id}`}
                          className={`relative rounded-2xl border p-5 transition-colors ${isSelected ? 'border-amber-400 bg-amber-500/5' : 'border-zinc-800 bg-zinc-950 hover:border-zinc-700'}`}>
                          <span className={`absolute top-4 right-4 text-[10px] uppercase tracking-widest px-2 py-1 rounded-full border ${meta.chip}`}>
                            {meta.label}
                          </span>
                          <h3 className="text-lg font-bold pr-20 leading-snug">{svc.name}</h3>
                          <p className="text-zinc-400 text-sm mt-2 leading-relaxed min-h-[3.5rem]">{svc.description}</p>
                          <div className="flex items-center gap-3 mt-4 text-xs text-zinc-500">
                            <Clock size={14} /> {svc.delivery}
                          </div>
                          <div className="flex items-end justify-between mt-5">
                            <span className="text-3xl font-black text-white">{svc.price.toFixed(0)}<span className="text-base text-zinc-500"> €</span></span>
                            <button
                              data-testid={`service-select-${svc.id}`}
                              onClick={() => handleSelect(svc)}
                              className={`text-sm font-bold px-4 py-2 rounded-full transition-colors ${isSelected ? 'bg-amber-400 text-black' : 'bg-white text-black hover:bg-amber-400'}`}>
                              {isSelected ? "Sélectionné" : "Choisir"}
                            </button>
                          </div>
                        </motion.div>
                      );
                    })}
                  </div>
                </section>
              );
            })}
          </div>
        )}

        {/* Booking form */}
        {selected && (
          <section id="booking-form" data-testid="booking-form-section"
            className="mt-16 rounded-3xl border border-amber-500/30 bg-gradient-to-br from-amber-500/5 via-zinc-950 to-zinc-950 p-6 sm:p-10">
            <p className="text-amber-400 text-xs tracking-[0.3em] uppercase mb-2">Étape 2 — Réservation</p>
            <h3 className="text-2xl sm:text-3xl font-black mb-2">{selected.name}</h3>
            <p className="text-zinc-400 mb-8">
              <b className="text-white">{selected.price.toFixed(2)} €</b> · livraison {selected.delivery}
            </p>

            {bookingResult ? (
              <div className="text-center py-8" data-testid="booking-success">
                <div className="w-20 h-20 mx-auto mb-4 rounded-full bg-emerald-500/15 border border-emerald-500/40 flex items-center justify-center">
                  <Check size={36} className="text-emerald-400" />
                </div>
                <h4 className="text-2xl font-bold mb-2">Réservation confirmée</h4>
                <p className="text-zinc-400 max-w-md mx-auto">
                  On t'a envoyé un email de confirmation à <b>{bookingResult.customer_email}</b>.
                  Notre équipe te recontacte sous 24h pour lancer le projet.
                </p>
                <p className="text-zinc-600 text-xs mt-4 font-mono">Réf : {bookingResult.paypal_order_id}</p>
              </div>
            ) : (
              <>
                <div className="grid sm:grid-cols-2 gap-4 mb-4">
                  <input data-testid="booking-name" placeholder="Ton nom (ou alias)" value={bookingState.name}
                    onChange={(e) => setBookingState({ ...bookingState, name: e.target.value })}
                    className="bg-black border border-zinc-800 rounded-xl px-4 py-3 text-white placeholder:text-zinc-600 focus:border-amber-400 focus:outline-none" />
                  <input data-testid="booking-email" type="email" placeholder="Email" value={bookingState.email}
                    onChange={(e) => setBookingState({ ...bookingState, email: e.target.value })}
                    className="bg-black border border-zinc-800 rounded-xl px-4 py-3 text-white placeholder:text-zinc-600 focus:border-amber-400 focus:outline-none" />
                </div>
                <input data-testid="booking-project" placeholder="Titre du projet (optionnel)" value={bookingState.project}
                  onChange={(e) => setBookingState({ ...bookingState, project: e.target.value })}
                  className="w-full bg-black border border-zinc-800 rounded-xl px-4 py-3 text-white placeholder:text-zinc-600 focus:border-amber-400 focus:outline-none mb-4" />
                <textarea data-testid="booking-notes" placeholder="Notes : nombre de pistes, références sonores, deadline souhaitée…" value={bookingState.notes}
                  onChange={(e) => setBookingState({ ...bookingState, notes: e.target.value })}
                  rows={4}
                  className="w-full bg-black border border-zinc-800 rounded-xl px-4 py-3 text-white placeholder:text-zinc-600 focus:border-amber-400 focus:outline-none mb-6 resize-none" />

                <button
                  data-testid="booking-pay-btn"
                  disabled={!validForm}
                  onClick={() => setPaypalOpen(true)}
                  className={`w-full sm:w-auto px-8 py-4 rounded-full font-bold transition-colors ${validForm ? 'bg-amber-400 text-black hover:bg-amber-300' : 'bg-zinc-800 text-zinc-600 cursor-not-allowed'}`}>
                  Payer {selected.price.toFixed(2)} € avec PayPal →
                </button>
                {!validForm && <p className="text-zinc-500 text-xs mt-3">Nom + email valide requis pour activer le paiement.</p>}
              </>
            )}
          </section>
        )}

        {paypalOpen && selected && (
          <PayPalCheckoutModal
            product={{ id: selected.id, name: selected.name, price: selected.price }}
            productType="service_pro"
            onClose={() => setPaypalOpen(false)}
            onBeforeCapture={async (orderId) => {
              const booking = await registerBooking(orderId);
              setBookingResult(booking);
            }}
            onSuccess={() => {
              setPaypalOpen(false);
            }}
          />
        )}
      </div>
    </main>
  );
}
